// Common Routes Configuration
import { lazy } from 'react';

// Lazy load common components
const HomePage = lazy(() => import('../../shared/components/ui/HomePage'));
const UnifiedLogin = lazy(() => import('../../shared/components/auth/UnifiedLogin'));
const ResetPassword = lazy(() => import('../../shared/components/auth/ResetPassword'));
const UnauthorizedPage = lazy(() => import('../../shared/components/ui/UnauthorizedPage'));
const NotFoundPage = lazy(() => import('../../shared/components/ui/NotFoundPage'));
const ProfileSettings = lazy(() => import('../../shared/components/profile/ProfileSettings'));
const HelpCenter = lazy(() => import('../../shared/components/ui/HelpCenter'));
const ContactSupport = lazy(() => import('../../shared/components/ui/ContactSupport'));
const ClientIntakeForm = lazy(() => import('../../pages/ClientIntakeForm'));

/**
 * Common Routes Configuration
 * Public routes and shared functionality
 */
export const commonRoutes = [
  // Public routes
  {
    path: '/',
    component: HomePage,
    name: 'Home',
    description: 'Application home page',
    isPublic: true,
    meta: {
      title: 'FleetCam - Fleet Management System',
      description: 'Advanced fleet monitoring and management platform',
    },
  },
  {
    path: '/login',
    component: UnifiedLogin,
    name: 'Login',
    description: 'User authentication page',
    isPublic: true,
    meta: {
      title: 'Login - FleetCam',
      hideNavigation: true,
    },
  },
  {
    path: '/reset-password/:uidb64/:token',
    component: ResetPassword,
    name: 'Reset Password',
    description: 'Password reset page',
    isPublic: true,
    meta: {
      title: 'Reset Password - FleetCam',
      hideNavigation: true,
    },
  },

  // Public client intake form (token-based, no auth)
  {
    path: '/client-intake/:token',
    component: ClientIntakeForm,
    name: 'Client Intake',
    isPublic: true,
    meta: {
      title: 'Client Intake - FleetCam',
      hideNavigation: true,
    },
  },

  // Error and utility routes
  {
    path: '/unauthorized',
    component: UnauthorizedPage,
    name: 'Unauthorized',
    description: 'Access denied page',
    isPublic: true,
    meta: {
      title: 'Access Denied - FleetCam',
      hideNavigation: true,
    },
  },
  {
    path: '/404',
    component: NotFoundPage,
    name: 'Not Found',
    description: 'Page not found',
    isPublic: true,
    meta: {
      title: 'Page Not Found - FleetCam',
      hideNavigation: true,
    },
  },
  {
    path: '*',
    component: NotFoundPage,
    name: 'Catch All',
    description: 'Catch all route for 404',
    isPublic: true,
    meta: {
      title: 'Page Not Found - FleetCam',
      hideNavigation: true,
    },
  },

  // Authenticated common routes
  {
    path: '/profile',
    component: ProfileSettings,
    name: 'Profile Settings',
    description: 'User profile management',
    requiredPermissions: ['profile.view'],
    meta: {
      title: 'Profile Settings - FleetCam',
      breadcrumb: 'Profile',
      icon: 'account_circle',
    },
  },
  {
    path: '/help',
    component: HelpCenter,
    name: 'Help Center',
    description: 'Help and documentation',
    isPublic: true,
    meta: {
      title: 'Help Center - FleetCam',
      breadcrumb: 'Help',
      icon: 'help',
    },
  },
  {
    path: '/support',
    component: ContactSupport,
    name: 'Contact Support',
    description: 'Contact support team',
    requiredPermissions: ['support.access'],
    meta: {
      title: 'Contact Support - FleetCam',
      breadcrumb: 'Support',
      icon: 'support_agent',
    },
  },
];

/**
 * Portal redirect routes based on user roles
 */
export const portalRedirects = {
  'Super Admin': '/super-admin',
  Admin: '/admin',
  'Bureau Agent': '/bureau',
  'Bureau Agents Staff': '/bureau',
  'TipLock Staff': '/tiplock',
  'TipLock Agent': '/tiplock',
  'Client User': '/client',
  Client: '/client',
  Supervisor: '/bureau',
  User: '/client',
  Manager: '/admin',
  'Sales Rep': '/sales',
  Sales: '/sales',
};

/**
 * Default redirect logic based on user roles and assigned portal
 */
export const getDefaultRedirectForUser = (user) => {
  // console.log('[getDefaultRedirectForUser] Input user:', user);

  if (!user) {
    return '/login';
  }

  // PRIORITY 1: Check assigned_portal field first (this is the new RBAC way)
  if (user.assigned_portal) {
    console.log('[getDefaultRedirectForUser] Using assigned_portal:', user.assigned_portal);
    switch (user.assigned_portal) {
      case 'super-admin':
      case 'superadmin':
        return '/super-admin';
      case 'admin':
        return '/admin';
      case 'bureau':
        return '/bureau';
      case 'client':
        return '/client';
      case 'tiplock':
        return '/tiplock';
      case 'sales':
        return '/sales';
      case 'technician':
        return '/technician';
      default:
        console.log('[getDefaultRedirectForUser] Unknown assigned_portal:', user.assigned_portal);
    }
  }

  // PRIORITY 2: Fallback to role-based redirect for backward compatibility
  // Get roles from both legacy groups and new RBAC roles
  let userRoles = [];

  // Legacy groups support
  if (user.groups && user.groups.length > 0) {
    userRoles = user.groups.map((group) => (typeof group === 'string' ? group : group.name));
  }

  // New RBAC roles support
  if (user.roles && user.roles.length > 0) {
    const rbacRoles = user.roles.map((role) => (typeof role === 'string' ? role : role.name));
    userRoles = [...userRoles, ...rbacRoles];
  }

  // console.log('[getDefaultRedirectForUser] Extracted roles:', userRoles);
  // console.log('[getDefaultRedirectForUser] Portal redirects:', portalRedirects);

  // Priority order: Super Admin > Admin > Bureau > Client
  const rolePriority = [
    'Super Admin',
    'Admin',
    'Manager',
    'Bureau Agent',
    'Bureau Agents Staff',
    'Supervisor',
    'Client User',
    'Client',
    'User',
  ];

  for (const role of rolePriority) {
    if (userRoles.includes(role)) {
      const redirect = portalRedirects[role] || '/';
      // console.log('[getDefaultRedirectForUser] Found matching role:', role, '-> Redirecting to:', redirect);
      return redirect;
    }
  }

  // If no role match, check if we have any roles and return a default
  if (userRoles.length > 0) {
    // console.log('[getDefaultRedirectForUser] User has roles but no match, defaulting to /client');
    return '/client'; // Default to client portal for unknown roles
  }

  // console.log('[getDefaultRedirectForUser] No roles found, returning /unauthorized');
  return '/unauthorized';
};

/**
 * Route access validation
 */
export const validateRouteAccess = (route, user, permissions) => {
  // Public routes are always accessible
  if (route.isPublic) {
    return true;
  }

  // Must be authenticated for protected routes
  if (!user) {
    return false;
  }

  // Check required roles
  if (route.requiredRoles && route.requiredRoles.length > 0) {
    const userRoles =
      user.groups?.map((group) => (typeof group === 'string' ? group : group.name)) || [];

    const hasRequiredRole = route.requiredRoles.some((role) => userRoles.includes(role));

    if (!hasRequiredRole) {
      return false;
    }
  }

  // Check required permissions
  if (route.requiredPermissions && route.requiredPermissions.length > 0) {
    const hasRequiredPermissions = route.requiredPermissions.every((permission) =>
      permissions.includes(permission)
    );

    if (!hasRequiredPermissions) {
      return false;
    }
  }

  return true;
};

export default commonRoutes;
